import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/apiClient';
import { Shield, Users, Crown, Calendar, Target, Trash2 } from 'lucide-react';

export default function AdminPage() {
    const [users, setUsers] = useState([]);
    const navigate = useNavigate();

    const fetchData = async () => {
        try {
            const uRes = await api.get('/auth/users');
            setUsers(uRes.data);
        } catch (err) {
            if (err.response?.status === 403) navigate('/dashboard');
        }
    };

    useEffect(() => { fetchData(); }, []);

    const toggleStatus = async (userId) => {
        try {
            const res = await api.put(`/auth/users/${userId}/toggle-status`);
            setUsers(users.map(u => u.id === userId ? { ...u, is_active: res.data.is_active } : u));
        } catch (err) { alert("Erreur lors de la modification du statut."); }
    };

    const togglePro = async (userId) => {
        if (!window.confirm("Voulez-vous modifier le statut PRO de cet utilisateur ?")) return;
        try {
            const res = await api.put(`/auth/users/${userId}/toggle-pro`);
            setUsers(users.map(u => u.id === userId ? { ...u, is_pro: res.data.is_pro } : u));
        } catch (err) { 
            alert("Erreur lors de la modification du statut PRO. Vérifiez que l'endpoint backend existe."); 
        }
    };

    const deleteUser = async (userId, email) => {
        if (!window.confirm(`⚠️ ATTENTION : Voulez-vous vraiment supprimer définitivement l'utilisateur "${email}" de la base de données ? Cette action est irréversible.`)) return;
        try {
            // Appel de la nouvelle route isolée
            await api.delete(`/auth/admin/users/${userId}`);
            setUsers(users.filter(u => u.id !== userId));
        } catch (err) {
            // Affichage de l'erreur exacte renvoyée par le backend
            const msg = err.response?.data?.detail || "Erreur lors de la suppression de l'utilisateur. Vérifiez vos permissions.";
            alert(msg);
        }
    };

    const getAvatarConfig = (email) => {
        const letter = email.charAt(0).toUpperCase();
        const colors = [
            'bg-blue-100 text-blue-700 border-blue-200',
            'bg-emerald-100 text-emerald-700 border-emerald-200',
            'bg-rose-100 text-rose-700 border-rose-200',
            'bg-amber-100 text-amber-700 border-amber-200',
            'bg-purple-100 text-purple-700 border-purple-200'
        ];
        const code = letter.charCodeAt(0) || 0;
        return { letter, colorClass: colors[code % colors.length] };
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Date inconnue";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Date inconnue";
            return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch (e) {
            return "Date inconnue";
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                <div className="bg-slate-900 rounded-3xl p-8 mb-6 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden relative">
                    <div className="absolute -right-20 -top-20 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px]"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 bg-indigo-500/20 rounded-2xl border border-indigo-400/30">
                            <Shield size={32} className="text-indigo-400" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight">Panel Administrateur</h1>
                            <p className="text-indigo-200 font-medium">Gérez les accès et abonnements de vos membres.</p>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 mb-6">
                    <div className="px-6 py-3 rounded-2xl font-black flex items-center gap-2 transition-colors bg-indigo-600 text-white shadow-md shadow-indigo-600/20 cursor-default">
                        <Users size={18}/> Membres Inscrits ({users.length})
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Utilisateur</th>
                                    <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Inscription</th>
                                    <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Abonnement</th>
                                    <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Statut</th>
                                    <th className="px-6 py-4 text-center font-black text-slate-400 uppercase tracking-widest text-[10px]">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {users.map(u => {
                                    const avatar = getAvatarConfig(u.email);
                                    return (
                                        <tr key={u.id} className="hover:bg-slate-50/50 transition-colors group">
                                            
                                            <td className="px-6 py-5">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl border shadow-sm ${avatar.colorClass}`}>
                                                        {avatar.letter}
                                                    </div>
                                                    <div>
                                                        <div className="font-black text-slate-900 text-base">{u.email}</div>
                                                        <div className="text-xs font-medium text-slate-400 mt-1 flex items-center gap-1.5">
                                                            <Target size={12}/> {u.objective || "Aucun objectif renseigné"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="px-6 py-5">
                                                <div className="flex items-center gap-2 text-slate-600 font-medium text-sm">
                                                    <Calendar size={14} className="text-slate-400" />
                                                    {formatDate(u.created_at)}
                                                </div>
                                            </td>

                                            <td className="px-6 py-5">
                                                <div className="flex items-center gap-2">
                                                    {u.is_pro ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm border border-purple-500">
                                                            <Crown size={12} className="fill-white/20" /> Premium
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 border border-slate-200">
                                                            Gratuit
                                                        </span>
                                                    )}
                                                    
                                                    {!u.is_admin && (
                                                        <button onClick={() => togglePro(u.id)} className="text-[10px] text-indigo-400 hover:text-indigo-600 font-bold uppercase tracking-wider ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            Modifier
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="px-6 py-5">
                                                {u.is_active ? (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-200">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]"></span> Actif
                                                    </span> 
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-600 border border-amber-200">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span> En attente
                                                    </span>
                                                )}
                                            </td>

                                            <td className="px-6 py-5 text-center">
                                                {!u.is_admin ? (
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button 
                                                            onClick={() => toggleStatus(u.id)} 
                                                            className={`font-black px-5 py-2 rounded-xl transition-all text-xs uppercase tracking-widest border shadow-sm ${u.is_active ? 'text-rose-600 bg-white border-rose-200 hover:bg-rose-50 hover:border-rose-300' : 'text-white bg-indigo-600 border-indigo-700 hover:bg-indigo-700 hover:shadow-indigo-600/30'}`}
                                                        >
                                                            {u.is_active ? 'Suspendre' : 'Approuver'}
                                                        </button>
                                                        <button 
                                                            onClick={() => deleteUser(u.id, u.email)}
                                                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors border border-transparent hover:border-rose-200"
                                                            title="Supprimer définitivement cet utilisateur"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-4 py-2 rounded-xl border border-slate-200">
                                                        Admin
                                                    </span>
                                                )}
                                            </td>

                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
}
